import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import type { TenantListResponseDto, TenantSummaryDto } from '@org/contracts';

import { tenantKeys } from '@/api/tenant-keys';

/**
 * A tenant's name from what the clinics list or a summary already loaded,
 * or null. It never fetches. Names hold no PHI.
 */
export function tenantNameFromCache(queryClient: QueryClient, tenantId: string): string | null {
  const key = tenantId.toLowerCase();
  const summary = queryClient.getQueryData<TenantSummaryDto>(tenantKeys.summary(key));
  if (summary !== undefined) {
    return summary.name;
  }
  const list = queryClient.getQueryData<TenantListResponseDto>(tenantKeys.list);
  const item = list?.items.find((tenant) => {
    return tenant.id.toLowerCase() === key;
  });
  return item?.name ?? null;
}

function isNameKey(queryKey: readonly unknown[]): boolean {
  if (queryKey[0] === tenantKeys.list[0]) {
    return true;
  }
  return queryKey[0] === 'tenant' && queryKey[2] === 'summary';
}

/**
 * Lookup for clinic names, kept fresh as the list and summaries load. Used
 * by the shell (banner, step-up dialog, frame title) to replace the short id.
 */
export function useTenantNames(): (tenantId: string) => string | null {
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' && event.type !== 'removed') {
        return;
      }
      const { queryKey } = event.query as { queryKey: readonly unknown[] };
      if (!isNameKey(queryKey)) {
        return;
      }
      setVersion((previous) => {
        return previous + 1;
      });
    });
  }, [queryClient]);

  return useCallback(
    (tenantId: string) => {
      return tenantNameFromCache(queryClient, tenantId);
    },
    // `version` changes when a name may have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, version],
  );
}
